import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, AlertCircle, Loader2 } from "lucide-react";
import { ENQUIRY_TYPES, BRAND_OPTIONS } from "@/config/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+0-9 ()-]{7,20}$/;

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid business email.";
  if (!PHONE_RE.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!f.enquiry_type) e.enquiry_type = "Please choose an enquiry type.";
  if (!f.brand_interest) e.brand_interest = "Please choose a brand.";
  if (f.message.trim().length < 10) e.message = "Please share a few details (at least 10 characters).";
  if (!f.consent) e.consent = "Please accept the privacy policy to continue.";
  return e;
};

const Field = ({ id, label, required, error, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="eyebrow !text-[0.68rem] block">
      {label} {required && <span className="text-rose-ink" aria-hidden="true">*</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} className="mt-2 text-sm text-rose-ink" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const EnquiryForm = ({ defaultType = "", defaultBrand = "", testId = "enquiry-form" }) => {
  const [f, setF] = useState({
    full_name: "", company_name: "", email: "", phone: "",
    enquiry_type: defaultType, brand_interest: defaultBrand, message: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverMsg, setServerMsg] = useState("");
  const [ref, setRef] = useState("");

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const a11y = (k) => ({ "aria-invalid": errors[k] ? "true" : "false", "aria-describedby": errors[k] ? `${testId}-${k}-error` : undefined });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!API) {
      setStatus("error");
      setServerMsg("The enquiry form is awaiting integration. Please try again later.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/enquiries`, { ...f, email: f.email.trim() });
      setRef(res.data?.id ?? "");
      setStatus("success");
    } catch (err) {
      const d = err.response?.data?.detail;
      setServerMsg(typeof d === "string" ? d : "We couldn't submit your enquiry. Please check your details and try again.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border hairline bg-white p-8 md:p-12" role="status" data-testid={`${testId}-success`}>
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--ink)] text-white"><Check size={22} /></span>
        <h3 className="font-display mt-6 text-3xl">Thank you. Your enquiry has been received.</h3>
        <p className="mt-3 max-w-md leading-relaxed text-[var(--ink-2)]">Our team will review your message and respond to the email address you provided.</p>
        {ref && <p className="mt-6 font-mono-label text-xs text-[var(--ink-2)]" data-testid={`${testId}-reference`}>Reference: {ref}</p>}
      </motion.div>
    );
  }

  const id = (k) => `${testId}-${k}`;
  return (
    <form onSubmit={submit} noValidate className="grid gap-x-8 gap-y-8 md:grid-cols-2" data-testid={testId}>
      <Field id={id("full_name")} label="Full Name" required error={errors.full_name}>
        <input id={id("full_name")} className="field" autoComplete="name" value={f.full_name} onChange={set("full_name")} {...a11y("full_name")} data-testid={`${testId}-name-input`} />
      </Field>
      <Field id={id("company_name")} label="Company Name">
        <input id={id("company_name")} className="field" autoComplete="organization" value={f.company_name} onChange={set("company_name")} data-testid={`${testId}-company-input`} />
      </Field>
      <Field id={id("email")} label="Business Email" required error={errors.email}>
        <input id={id("email")} type="email" className="field" autoComplete="email" value={f.email} onChange={set("email")} {...a11y("email")} data-testid={`${testId}-email-input`} />
      </Field>
      <Field id={id("phone")} label="Phone Number" required error={errors.phone}>
        <input id={id("phone")} type="tel" className="field" autoComplete="tel" value={f.phone} onChange={set("phone")} {...a11y("phone")} data-testid={`${testId}-phone-input`} />
      </Field>
      <Field id={id("enquiry_type")} label="Enquiry Type" required error={errors.enquiry_type}>
        <select id={id("enquiry_type")} className="field cursor-pointer" value={f.enquiry_type} onChange={set("enquiry_type")} {...a11y("enquiry_type")} data-testid={`${testId}-type-select`}>
          <option value="">Select an enquiry type</option>
          {ENQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field id={id("brand_interest")} label="Brand of Interest" required error={errors.brand_interest}>
        <select id={id("brand_interest")} className="field cursor-pointer" value={f.brand_interest} onChange={set("brand_interest")} {...a11y("brand_interest")} data-testid={`${testId}-brand-select`}>
          <option value="">Select a brand</option>
          {BRAND_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </Field>
      <Field id={id("message")} label="Message" required error={errors.message} className="md:col-span-2">
        <textarea id={id("message")} rows={4} className="field resize-y" value={f.message} onChange={set("message")} {...a11y("message")} data-testid={`${testId}-message-input`} />
      </Field>
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor={id("website")}>Website</label>
        <input id={id("website")} tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
      </div>
      <div className="md:col-span-2">
        <label className="flex items-start gap-3 text-[0.95rem] leading-relaxed text-[var(--ink-2)]">
          <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[var(--ink)]" {...a11y("consent")} data-testid={`${testId}-consent-checkbox`} />
          <span>I agree that Suvirika Retail Pvt Ltd may use the details above to respond to my enquiry, as described in the <Link to="/privacy-policy" className="underline underline-offset-4">Privacy Policy</Link>.</span>
        </label>
        {errors.consent && <p id={`${testId}-consent-error`} className="mt-2 text-sm text-rose-ink" data-testid={`${testId}-consent-error`}>{errors.consent}</p>}
      </div>
      {status === "error" && (
        <div className="md:col-span-2 flex items-start gap-3 border border-roseink/40 bg-[#fdf0f5] p-4 text-sm text-rose-ink" role="alert" data-testid={`${testId}-error`}>
          <AlertCircle size={18} className="mt-0.5 shrink-0" /> {serverMsg}
        </div>
      )}
      <div className="md:col-span-2">
        <button type="submit" disabled={status === "loading"} className="btn-ink disabled:opacity-60" data-testid={`${testId}-submit-button`}>
          {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Start a Conversation <ArrowRight size={18} /></>}
        </button>
      </div>
    </form>
  );
};
